import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';

import styles from './cem-color-scheme-toggle.css' with { type: 'css' };

type ColorScheme = 'light' | 'dark' | 'system';

/**
 * Toggle between light, dark, and system color schemes.
 *
 * @customElement cem-color-scheme-toggle
 */
@customElement('cem-color-scheme-toggle')
export class CemColorSchemeToggle extends LitElement {
  static styles = styles;

  /** Storage access gatekeeper - localStorage can throw in Safari private mode */
  #getStorageItem(key: string, defaultValue: string): string {
    try {
      return localStorage.getItem(key) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }

  #setStorageItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage unavailable (private mode), silently continue
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    // Restore saved color scheme preference
    const saved = this.#getStorageItem('cem-serve-color-scheme', 'system') as ColorScheme;
    this.#applyColorScheme(saved);
  }

  render() {
    const saved = this.#getStorageItem('cem-serve-color-scheme', 'system');
    return html`
      <div id="toggle-group"
           role="radiogroup"
           aria-label="Color scheme">
        <label class="toggle-option">
          <input type="radio"
                 name="color-scheme"
                 value="light"
                 .checked=${saved === 'light'}
                 @change=${this.#onChange}>
          <span class="toggle-label">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M8 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM8 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 0zm0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13zm8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5zM3 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8zm10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0zm-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0zm9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .707zM4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708z"/>
            </svg>
            <span class="sr-only">Light</span>
          </span>
        </label>
        <label class="toggle-option">
          <input type="radio"
                 name="color-scheme"
                 value="system"
                 .checked=${saved === 'system'}
                 @change=${this.#onChange}>
          <span class="toggle-label">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zm8 0A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm-8 8A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm8 0A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3z"/>
            </svg>
            <span class="sr-only">System</span>
          </span>
        </label>
        <label class="toggle-option">
          <input type="radio"
                 name="color-scheme"
                 value="dark"
                 .checked=${saved === 'dark'}
                 @change=${this.#onChange}>
          <span class="toggle-label">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M6 .278a.768.768 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277.527 0 1.04-.055 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z"/>
            </svg>
            <span class="sr-only">Dark</span>
          </span>
        </label>
      </div>
    `;
  }

  #onChange(e: Event): void {
    const target = e.target as HTMLInputElement;
    if (target.checked) {
      this.#applyColorScheme(target.value as ColorScheme);
    }
  }

  #applyColorScheme(scheme: ColorScheme): void {
    switch (scheme) {
      case 'light':
        document.body.style.colorScheme = 'light';
        break;
      case 'dark':
        document.body.style.colorScheme = 'dark';
        break;
      case 'system':
      default:
        document.body.style.colorScheme = 'light dark';
        break;
    }
    this.#setStorageItem('cem-serve-color-scheme', scheme);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cem-color-scheme-toggle': CemColorSchemeToggle;
  }
}
